import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Divider,
  Paper,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Search } from '@mui/icons-material';

import { HEADER_HEIGHT } from '@frontend/features/common/constants/layout';

import {
  SEARCH_FIELD_GAP_PX,
  desktopFieldMinWidth,
  mobileFieldBasis,
} from '../searchFieldLayout';

interface FieldProps {
  label: string;
  /** Ancho en desktop. */
  width?: number | string;
  /**
   * Qué FRACCIÓN del renglón ocupa en mobile (`0.6` = seis décimos). Por
   * defecto el campo se lleva el renglón entero; se declara acá para que dos
   * campos cortos que se leen juntos —una fecha y su hora, residencia y forma
   * de pago— compartan renglón en el celular en vez de dejar medio vacío.
   *
   * Es una fracción y no un ancho porque el ancho real tiene que descontar el
   * `gap` que la barra pone entre campos: ver `searchFieldLayout.ts`.
   */
  xsShare?: number;
  /** Crece para repartir el espacio sobrante (destinos, que son largos). */
  grow?: boolean;
  children: ReactNode;
}

/**
 * Un campo de la barra: la etiqueta arriba, chica, y el control sin marco
 * abajo. El marco lo pone la barra una sola vez, así ocho campos con borde no
 * compiten entre sí.
 *
 * El control CONSERVA su `label`: es su nombre accesible. Acá se oculta a la
 * vista y se redibuja arriba, así ningún input necesita un modo especial.
 */
export function SearchField({ label, width, xsShare, grow, children }: FieldProps) {
  return (
    <Box
      sx={(theme) => ({
        px: 1.25,
        py: 1,
        // En desktop un campo fijo conserva su ancho y uno largo puede ceder
        // solo hasta un piso legible. Si falta más lugar, la barra envuelve:
        // nunca recorta fechas, horas o selects para sostener una sola fila.
        minWidth: { xs: 0, md: desktopFieldMinWidth(width, Boolean(grow)) },
        // El padding entra DENTRO del ancho: si no, un campo al 50% mide 50%
        // más 32px y dos no entran nunca en la misma fila.
        boxSizing: 'border-box',
        width: { xs: 'auto', md: width },
        // En mobile el tamaño lo pone `flex-basis` (la fracción del renglón ya
        // con el gap descontado) y no `width`: es la medida contra la que
        // flexbox decide el wrap, así que es la única que evita que un par
        // declarado se parta. `maxWidth` acota el crecimiento al renglón.
        flexBasis: { xs: mobileFieldBasis(xsShare), md: 'auto' },
        // `maxWidth` además de `width`: sin esto el ancho mínimo del control
        // (un input de fecha no baja de ~200px) le gana al porcentaje y el
        // campo se va a la fila siguiente. Es lo mismo que hace `Grid`.
        maxWidth: { xs: '100%', md: 'none' },
        // En mobile SIEMPRE crece: el sobrante que dejó el descuento del gap
        // vuelve al par (60/40 exactos) y un campo que quedó solo en su
        // renglón lo llena, como se ven los campos que no piden fracción.
        flexGrow: { xs: 1, md: grow ? 1 : 0 },
        flexShrink: { xs: 1, md: grow ? 1 : 0 },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 0.25,
        minHeight: { xs: 64, md: 60 },
        borderRadius: 1,
        backgroundColor: alpha(theme.palette.common.black, 0.018),
        border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
        transition: theme.transitions.create(['background-color', 'box-shadow'], {
          duration: theme.transitions.duration.shortest,
        }),
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.035),
        },
        // Se le saca el cromo SOLO a los controles que están acá adentro:
        // nada de selectores globales que después rompen otro formulario.
        // El theme del proyecto pone el marco y el fondo en el ROOT del input
        // (no en el `notchedOutline`), así que hay que apagar los dos.
        '& .MuiOutlinedInput-notchedOutline': { border: 0 },
        '& .MuiOutlinedInput-root': {
          p: 0,
          pr: 0,
          border: 0,
          backgroundColor: 'transparent',
        },
        '& .MuiInputBase-input': { p: 0, fontSize: 14.5, fontWeight: 600 },
        '& .MuiAutocomplete-root .MuiOutlinedInput-root': { py: 0, pl: 0 },
        '& .MuiAutocomplete-endAdornment': { right: 0 },
        '& .MuiSelect-select': { p: 0, fontSize: 14.5, fontWeight: 600 },
        // Objetivo de pulgar: el control es lo ÚNICO que se puede tocar del
        // campo —la etiqueta de arriba es decorativa (`aria-hidden`) y la
        // real está oculta—, y a 21px de alto no llega a los 44 que pide una
        // pantalla táctil. La caja ya reservaba ese aire: el control pasa a
        // ocuparlo, no lo agrega. Solo en mobile, y con la clase repetida
        // donde MUI declara su propio mínimo y le gana a un selector simple.
        [theme.breakpoints.down('md')]: {
          '& .MuiInputBase-input': { minHeight: 44, boxSizing: 'border-box' },
          '& .MuiSelect-select.MuiSelect-select': {
            minHeight: 44,
            boxSizing: 'border-box',
            // El desplegable dibuja su valor en un `div`, que no centra el
            // texto solo como sí hace un `input`.
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiAutocomplete-root .MuiOutlinedInput-root.MuiOutlinedInput-root': {
            py: 0,
          },
        },
        // La etiqueta del control sigue existiendo para el lector de pantalla
        // (es el nombre accesible del input); solo se oculta a la vista, que
        // ya la muestra este componente. Así ningún input necesita un modo
        // especial para entrar a la barra.
        '& .MuiInputLabel-root': {
          position: 'absolute',
          width: 1,
          height: 1,
          p: 0,
          m: '-1px',
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
          border: 0,
        },
        '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
        // Al enfocar, el theme vuelve a dibujar el marco entero del input: en
        // una barra sin marcos eso es un cajón que aparece de la nada. Se
        // reemplaza por un subrayado — sigue siendo un indicador de foco
        // visible y con contraste, que es lo que hace falta.
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: 0,
        },
        '&:focus-within': {
          backgroundColor: alpha(theme.palette.primary.main, 0.035),
          boxShadow: `inset 0 -2px 0 ${theme.palette.primary.main}`,
        },
      })}
    >
      <Typography
        aria-hidden
        noWrap
        variant="caption"
        color="text.secondary"
        sx={{ lineHeight: 1.2, letterSpacing: '0.04em' }}
        title={label}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

/**
 * Título de un bloque dentro de la barra. Ocupa el ancho completo, así que
 * además ROMPE la fila: sirve para agrupar campos que van juntos (los datos de
 * un punto de traslado) sin sacarlos de la barra.
 */
export function SearchSection({
  label,
  action,
}: {
  label: string;
  action?: ReactNode;
}) {
  return (
    <Box
      sx={(theme) => ({
        width: '100%',
        px: 1,
        pt: 1,
        pb: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.07)}`,
        '&:first-of-type': {
          borderTop: 0,
          pt: 0,
        },
      })}
    >
      <Typography
        variant="caption"
        fontWeight={700}
        color="primary"
        sx={{ textTransform: 'uppercase' }}
      >
        {label}
      </Typography>
      {action}
    </Box>
  );
}

interface Props {
  onSearch: () => void;
  loading?: boolean;
  /** Falta algo para poder buscar: el botón queda apagado. */
  disabled?: boolean;
  /** Se queda pegada bajo el header al scrollear los resultados. */
  sticky?: boolean;
  /**
   * Vacía la búsqueda. Sin esto, una búsqueda restaurada se vuelve una jaula:
   * para arrancar una nueva hay que borrar campo por campo. Se pasa
   * `undefined` cuando no hay nada que limpiar y el botón no se dibuja.
   */
  onClear?: () => void;
  /** Controles que no son campos: swap de origen/destino, toggles. */
  children: ReactNode;
}

/**
 * Renglón explícito para un buscador que necesita más de una línea en desktop.
 * Mantiene el mismo algoritmo responsive de `SearchField`: en mobile sus
 * campos siguen usando `xsShare`, sin duplicar una segunda composición.
 */
export function SearchRow({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: `${SEARCH_FIELD_GAP_PX}px`,
      }}
    >
      {children}
    </Box>
  );
}

/**
 * La barra de búsqueda, igual para los ocho verticales. Un asesor que aprendió
 * a buscar trenes tiene que saber buscar ferries sin volver a aprender nada:
 * los campos van siempre en el mismo orden (dónde → cuándo → quiénes) y el
 * botón siempre a la derecha.
 *
 * En mobile los campos se apilan y el botón ocupa el ancho: es la misma barra,
 * no una pantalla aparte que haya que mantener en paralelo.
 */
export function SearchBar({
  onSearch,
  loading,
  disabled,
  sticky = true,
  onClear,
  children,
}: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'stretch',
        overflow: 'hidden',
        borderRadius: 1,
        border: 0,
        bgcolor: 'transparent',
        boxShadow: 'none',
        ...(sticky
          ? {
            position: 'sticky',
            top: HEADER_HEIGHT,
            // Debajo del header, arriba de los resultados.
            zIndex: theme.zIndex.appBar - 2,
            backdropFilter: 'blur(8px)',
          }
          : {}),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          // Fila con wrap SIEMPRE: en mobile cada campo ocupa el renglón
          // entero y cae solo, pero así dos campos cortos (fecha + hora)
          // comparten renglón cuando piden su fracción con `xsShare`.
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          flex: 1,
          minWidth: 0,
          // El gap en píxeles y no en unidades del theme: `searchFieldLayout`
          // lo descuenta del ancho de cada campo, así que los dos lados tienen
          // que leer el MISMO número.
          gap: `${SEARCH_FIELD_GAP_PX}px`,
          p: 1,
          border: 1,
          borderColor: 'divider',
          borderRadius: { xs: 1, md: '8px 0 0 8px' },
          bgcolor: 'background.paper',
          boxShadow: (muiTheme) => `0 10px 28px ${alpha(muiTheme.palette.common.black, 0.05)}`,
        }}
        // Enter busca desde cualquier campo, como en todos los buscadores.
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !disabled && !loading) onSearch();
        }}
      >
        {children}
      </Box>

      {isDesktop && <Divider orientation="vertical" flexItem />}

      <Box
        sx={(muiTheme) => ({
          p: { xs: 1.25, md: 1 },
          flexShrink: 0,
          display: 'flex',
          flexDirection: { xs: 'row', md: 'column' },
          justifyContent: 'center',
          alignItems: 'center',
          gap: 0.5,
          minWidth: { md: 148 },
          border: 1,
          borderLeft: { xs: 1, md: 0 },
          borderColor: 'divider',
          borderRadius: { xs: 1, md: '0 8px 8px 0' },
          bgcolor: alpha(muiTheme.palette.primary.main, 0.045),
          boxShadow: {
            xs: 'none',
            md: `0 10px 28px ${alpha(muiTheme.palette.common.black, 0.05)}`,
          },
        })}
      >
        {onClear && (
          <Button size="small" sx={{ textTransform: 'none' }} onClick={onClear}>
            {t('travelServices.clearSearch')}
          </Button>
        )}
        <LoadingButton
          variant="contained"
          startIcon={<Search />}
          loading={loading}
          disabled={disabled}
          onClick={onSearch}
          sx={{
            width: { xs: '100%', md: '100%' },
            minHeight: 44,
            px: 2,
            borderRadius: 1,
          }}
        >
          {t('travelServices.search')}
        </LoadingButton>
      </Box>
    </Paper>
  );
}

/**
 * Separador entre campos. Se dibuja solo en desktop: apilados, la línea
 * horizontal entre cada campo ensucia más de lo que ordena.
 */
export function SearchDivider() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  if (!isDesktop) return null;
  return <Divider orientation="vertical" flexItem sx={{ my: 1 }} />;
}
